import type { ChangeEvent, KeyboardEvent } from 'react';
import { useState, useCallback } from 'react';

import { IconButton, Input, InputGroup, InputRightElement, rem } from '@vimeo/bokeh';
import { ArrowUp } from '@vimeo/bokeh-icons';

import * as finalCopy from '../../copy/final';

interface Props {
  /** Callback fired when the user hits enter button inside the input */
  onSubmit: (value: string) => void;
  placeholderText?: string;
  isDisabled?: boolean;
  onBackClick?: VoidFunction;
}

/**
 * Just a little text input so users can talk to the AI
 */
export const PromptInput = ({
  onSubmit,
  placeholderText = finalCopy.askQuestionAction,
  isDisabled = false,
}: Props) => {
  const [questionText, setQuestionText] = useState('');
  const [isComposing, setIsComposing] = useState(false);

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setQuestionText(event.target.value);
  }, []);

  const handleSubmit = useCallback(() => {
    setQuestionText('');
    onSubmit(questionText);
  }, [onSubmit, questionText]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter' && !isComposing) {
        handleSubmit();
      }
    },
    [handleSubmit, isComposing],
  );

  return (
    <InputGroup mt={rem(8)}>
      <Input
        autoFocus
        onChange={handleChange}
        onCompositionEnd={() => setIsComposing(false)}
        onCompositionStart={() => setIsComposing(true)}
        onKeyDown={handleKeyDown}
        maxLength={500}
        placeholder={placeholderText}
        value={questionText}
        variant={'default'}
        disabled={isDisabled}
      />
      <InputRightElement>
        <IconButton
          isDisabled={questionText.trim().length === 0}
          aria-label="Submit question"
          variant="tertiary"
          icon={<ArrowUp color={questionText.length > 0 ? 'text-primary' : 'text-secondary'} />}
          size="xs"
          onClick={handleSubmit}
        />
      </InputRightElement>
    </InputGroup>
  );
};
